/**
 * The compliance calendar's API client — spec §D, ADR-0010.
 *
 * A client per module, not a growing `lib/api.ts` (waves.md §1.1).
 *
 * **The rules are not duplicated here.** `app/domain/calendar.py` on the
 * service is the only validator, and `POST /items/check` is how this screen
 * asks it — including which period formats a draft may use, which the server
 * derives by *running* that validator rather than restating the rule. Two
 * validators that agree today disagree in a year, and the one the firm sees
 * would be the one that is wrong.
 *
 * The pure helpers at the bottom live beside the client rather than inside a
 * component so the screen's rules can be tested without rendering anything —
 * the shape `lib/api/firm.ts` set in Wave 1A and `services.ts` followed.
 */

import { api } from '../api'

// -- shapes ------------------------------------------------------------------

export type Frequency = 'monthly' | 'quarterly' | 'half_yearly' | 'yearly' | 'custom_months'
export type PeriodFormat = 'MM-YYYY' | 'YYYY-YY' | 'AY YYYY-YY'
export type DueRule = 'day_of_month' | 'last_day' | 'days_after_period_end'
export type RunKind = 'scheduled' | 'on_demand' | 'reconcile'
export type OccurrenceStatus = 'pending' | 'opened' | 'failed' | 'skipped'

/** What the screen shows per row. Computed by the server against *today*,
 *  because a period becomes overdue without anything being written. */
export type OccurrenceState = 'opened' | 'awaiting_case' | 'overdue' | 'skipped'

export interface PeriodPlan {
  period: string
  period_start: string
  period_end: string
  due_date: string
  opens_on: string
}

export interface CalendarItem {
  id: string
  name: string
  description: string | null
  service_id: string
  plan_id: string | null
  frequency: Frequency
  months: number[]
  period_format: PeriodFormat
  due_rule: DueRule
  due_month_offset: number
  due_day: number | null
  due_offset_days: number | null
  lead_days: number
  tag: string | null
  effective_from: string
  active: boolean
  created_at: string
  /** Present on the list read, which resolves the catalog for the screen. */
  service_name?: string
  service_is_recurring?: boolean
  plan_name?: string | null
  applies_to?: { kinds?: string[]; constitutions?: string[] }
  /** FR-D3 — "the items with their matching client counts". */
  matching_clients?: number
  next_periods?: PeriodPlan[]
}

export interface Occurrence {
  id: string
  item_id: string
  item_name: string
  client_id: string
  client_name: string
  period: string
  period_end: string
  due_date: string
  opens_on: string
  case_id: string | null
  status: OccurrenceStatus
  state: OccurrenceState
  attempts: number
  last_error: string | null
}

/**
 * FR-D3's counts — **three of the five**.
 *
 * *Completed* and *by staff* are the case's status and assignee, which are
 * Wave 3A's columns. They are absent from the payload rather than served as
 * zero: a zero that means "not implemented" is a number a firm plans against.
 */
export interface Summary {
  generated: number
  opened: number
  awaiting_case: number
  overdue: number
}

export interface Issue {
  field: string | null
  message: string
}

export interface CheckResult {
  ok: boolean
  errors: Issue[]
  warnings: Issue[]
  next_periods: PeriodPlan[]
  /** The exact list for **this** draft — the server derives it by running the
   *  validator, so the dropdown and the refusal cannot disagree. */
  period_formats: PeriodFormat[]
}

export interface ItemOutcome {
  item_id: string
  name: string
  clients_matched: number
  periods: number
  claimed: number
  already_present: number
  opened: number
  failed: number
  /** Claimed, waiting for the case engine to be wired (Wave 4A). **Not a
   *  failure**, and never reported as one. */
  awaiting_opener: number
  skipped_reason: string | null
}

export interface RunResult {
  run_id: string | null
  kind: RunKind
  window_start: string
  window_end: string
  claimed: number
  already_present: number
  opened: number
  failed: number
  awaiting_opener: number
  skipped_run: string | null
  items: ItemOutcome[]
}

export interface Run {
  id: string
  kind: RunKind
  scheduled_for: string | null
  window_start: string
  window_end: string
  item_id: string | null
  status: 'running' | 'completed' | 'failed'
  started_at: string
  finished_at: string | null
  items_considered: number
  items_skipped: number
  periods_considered: number
  occurrences_claimed: number
  already_present: number
  cases_opened: number
  cases_failed: number
  detail: { items?: ItemOutcome[] }
  error: string | null
  actor_user_id: string | null
}

export interface Vocabulary {
  frequencies: Frequency[]
  period_formats: PeriodFormat[]
  period_formats_for: Record<Frequency, PeriodFormat[]>
  due_rules: DueRule[]
  run_kinds: RunKind[]
  occurrence_statuses: OccurrenceStatus[]
  /** **Always empty.** ProDesk ships no statutory item (ADR-0010 §1); a
   *  starter list needs its own ADR. Typed so the screen can say so. */
  shipped_items: never[]
}

export interface ItemDraft {
  name: string
  service_id: string
  plan_id: string | null
  description: string | null
  frequency: Frequency
  months: number[]
  period_format: PeriodFormat
  due_rule: DueRule
  due_month_offset: number
  due_day: number | null
  due_offset_days: number | null
  lead_days: number
  tag: string | null
  effective_from: string | null
  active: boolean
}

// -- the client --------------------------------------------------------------

export const calendar = {
  vocabulary: () => api<Vocabulary>('/api/v1/calendar/vocabulary'),

  items: (active?: boolean) =>
    api<CalendarItem[]>(
      `/api/v1/calendar/items${active === undefined ? '' : `?active=${active}`}`,
    ),
  item: (id: string) => api<CalendarItem>(`/api/v1/calendar/items/${id}`),
  create: (body: Partial<ItemDraft>) =>
    api<CalendarItem>('/api/v1/calendar/items', { method: 'POST', body }),
  update: (id: string, body: Partial<ItemDraft>) =>
    api<CalendarItem>(`/api/v1/calendar/items/${id}`, { method: 'PATCH', body }),
  check: (body: Partial<ItemDraft>) =>
    api<CheckResult>('/api/v1/calendar/items/check', { method: 'POST', body }),

  periods: () => api<Array<{ period: string; period_end: string; total: number }>>(
    '/api/v1/calendar/periods',
  ),
  occurrences: (params: { period?: string; item_id?: string; client_id?: string } = {}) => {
    const query = new URLSearchParams()
    for (const [key, value] of Object.entries(params)) if (value) query.set(key, value)
    const suffix = query.toString() ? `?${query}` : ''
    return api<{ occurrences: Occurrence[]; summary: Summary }>(
      `/api/v1/calendar/occurrences${suffix}`,
    )
  },
  skip: (id: string, reason?: string) =>
    api<{ id: string; status: OccurrenceStatus }>(
      `/api/v1/calendar/occurrences/${id}/skip`,
      { method: 'POST', body: { reason: reason ?? null } },
    ),

  runs: () => api<Run[]>('/api/v1/calendar/runs'),
  generate: (body: { item_id?: string } = {}) =>
    api<RunResult>('/api/v1/calendar/generate', { method: 'POST', body }),
  reconcile: (body: { window_start?: string; window_end?: string } = {}) =>
    api<RunResult>('/api/v1/calendar/reconcile', { method: 'POST', body }),
}

// -- the screen's rules, as functions ----------------------------------------

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

export function monthName(month: number): string {
  return MONTHS[month - 1] ?? String(month)
}

export function frequencyLabel(frequency: Frequency, months: number[] = []): string {
  switch (frequency) {
    case 'monthly':
      return 'Every month'
    case 'quarterly':
      return 'Every quarter'
    case 'half_yearly':
      return 'Twice a year'
    case 'yearly':
      return 'Once a year'
    case 'custom_months':
      return months.length ? `In ${months.map(monthName).join(' and ')}` : 'Specific months'
    default:
      return frequency
  }
}

const ORDINALS: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd', 21: 'st', 22: 'nd', 23: 'rd', 31: 'st' }

export function ordinal(day: number): string {
  return `${day}${ORDINALS[day] ?? 'th'}`
}

/**
 * When this item is due, in a sentence.
 *
 * All three rules count from the **period's end**, never from the day the
 * case was generated — a case opened late by the reconciler is still due on
 * the statutory date, and the wording has to say so or a firm will read the
 * dates as relative to today.
 */
export function dueLabel(
  item: Pick<CalendarItem, 'due_rule' | 'due_month_offset' | 'due_day' | 'due_offset_days'>,
): string {
  const after =
    item.due_month_offset === 0
      ? 'the month the period ends'
      : item.due_month_offset === 1
        ? 'the month after the period ends'
        : `${item.due_month_offset} months after the period ends`

  switch (item.due_rule) {
    case 'day_of_month':
      return `The ${ordinal(item.due_day ?? 1)} of ${after}`
    case 'last_day':
      return `The last day of ${after}`
    case 'days_after_period_end':
      return item.due_offset_days === 0
        ? 'The day the period ends'
        : `${item.due_offset_days} days after the period ends`
    default:
      return item.due_rule
  }
}

export function leadLabel(leadDays: number): string {
  if (leadDays === 0) return 'Opens on the due date'
  if (leadDays === 1) return 'Opens 1 day before it is due'
  return `Opens ${leadDays} days before it is due`
}

/**
 * The four states a period can be in, and their words.
 *
 * `awaiting_case` is a real, ordinary state and not an error: the period was
 * claimed and the case has not been opened yet. Until Wave 4A wires the
 * opener it is *every* row, which is why it reads as neutral rather than as a
 * failure.
 */
export function stateLabel(state: OccurrenceState): string {
  switch (state) {
    case 'opened':
      return 'Case open'
    case 'overdue':
      return 'Overdue, no case'
    case 'skipped':
      return 'Skipped'
    default:
      return 'Awaiting case'
  }
}

export function stateTone(state: OccurrenceState): 'success' | 'error' | 'warning' | 'info' {
  switch (state) {
    case 'opened':
      return 'success'
    case 'overdue':
      return 'error'
    case 'skipped':
      return 'info'
    default:
      return 'warning'
  }
}

/** Errors sorted onto the field that caused them, so a message lands on the
 *  control the firm has to change rather than in a banner they cannot act on.
 *  Field-less issues come back under `''`. */
export function groupIssues(issues: Issue[]): Record<string, string[]> {
  const out: Record<string, string[]> = {}
  for (const issue of issues) {
    const key = issue.field ?? ''
    ;(out[key] ??= []).push(issue.message)
  }
  return out
}

/**
 * What a run did, in one line.
 *
 * The zero cases are the point. A firm pressing Generate and seeing nothing
 * needs to know *which* nothing happened — no items, no matching clients, no
 * period due yet — and "0 cases created" answers none of those. The counters
 * on the run row exist so this sentence can.
 */
export function runSummary(
  run: Pick<
    RunResult,
    'claimed' | 'already_present' | 'opened' | 'failed' | 'awaiting_opener' | 'items'
  >,
): string {
  if (run.items.length === 0) {
    return 'Nothing to do — you have no active calendar items.'
  }
  if (run.claimed === 0 && run.already_present === 0) {
    const matched = run.items.reduce((total, item) => total + item.clients_matched, 0)
    if (matched === 0) {
      return 'No client matched any of your items. Check who each service is for, and your tags.'
    }
    return 'No period was due to open in this window. Nothing was created.'
  }
  const parts: string[] = []
  if (run.claimed) parts.push(`${run.claimed} new ${run.claimed === 1 ? 'period' : 'periods'}`)
  if (run.already_present) parts.push(`${run.already_present} already there`)
  if (run.opened) parts.push(`${run.opened} ${run.opened === 1 ? 'case' : 'cases'} opened`)
  // `awaiting_opener` is deliberately absent from this sentence. It is a real
  // count and it is *not* a failure — the case engine is not wired yet — and
  // the periods say so for themselves on the table below, each reading
  // "Awaiting case". Naming it here would put an unexplained number in front
  // of a firm whose calendar is working exactly as designed.
  if (run.failed) parts.push(`${run.failed} could not open`)
  return `${parts.join(', ')}.`
}

/** A blank item for the editor. `lead_days` is 10 rather than 0 because 0 opens
 *  the case on the day it is due, which the server warns about — a default
 *  should not walk the firm into a warning. */
export function blankDraft(serviceId = ''): ItemDraft {
  return {
    name: '',
    service_id: serviceId,
    plan_id: null,
    description: null,
    frequency: 'monthly',
    months: [],
    period_format: 'MM-YYYY',
    due_rule: 'day_of_month',
    due_month_offset: 1,
    due_day: 20,
    due_offset_days: null,
    lead_days: 10,
    tag: null,
    effective_from: null,
    active: true,
  }
}

/** The draft an existing item edits from. */
export function toDraft(item: CalendarItem): ItemDraft {
  return {
    name: item.name,
    service_id: item.service_id,
    plan_id: item.plan_id,
    description: item.description,
    frequency: item.frequency,
    months: [...item.months],
    period_format: item.period_format,
    due_rule: item.due_rule,
    due_month_offset: item.due_month_offset,
    due_day: item.due_day,
    due_offset_days: item.due_offset_days,
    lead_days: item.lead_days,
    tag: item.tag,
    effective_from: item.effective_from,
    active: item.active,
  }
}

/**
 * Switching the due rule clears the other rules' parameters.
 *
 * The server refuses a row carrying two of them — a day-of-month rule with a
 * days-after offset is a due date computed from a default nobody chose — so
 * the form must not carry the old one across, or the firm gets a refusal for
 * a field the screen is no longer showing them.
 */
export function withDueRule(draft: ItemDraft, rule: DueRule): ItemDraft {
  return {
    ...draft,
    due_rule: rule,
    due_day: rule === 'day_of_month' ? (draft.due_day ?? 20) : null,
    due_offset_days: rule === 'days_after_period_end' ? (draft.due_offset_days ?? 15) : null,
  }
}

/** Switching the frequency: `months` belongs to `custom_months` alone, and a
 *  period format the new frequency cannot use has to go too. */
export function withFrequency(
  draft: ItemDraft,
  frequency: Frequency,
  allowed: PeriodFormat[],
): ItemDraft {
  return {
    ...draft,
    frequency,
    months: frequency === 'custom_months' ? draft.months : [],
    period_format: allowed.includes(draft.period_format) ? draft.period_format : (allowed[0] ?? 'MM-YYYY'),
  }
}

export function toggleMonth(months: number[], month: number): number[] {
  return months.includes(month)
    ? months.filter((m) => m !== month)
    : [...months, month].sort((a, b) => a - b)
}

/** Who a service is offered to, in the firm's words — 2B's `applies_to`, whose
 *  **empty object means everyone**. Shown on the item because that is what
 *  decides which clients the calendar generates for. */
export function appliesToLabel(appliesTo: CalendarItem['applies_to'], tag: string | null): string {
  const parts: string[] = []
  const kinds = appliesTo?.kinds ?? []
  const constitutions = appliesTo?.constitutions ?? []
  if (kinds.length) parts.push(kinds.join(' and '))
  if (constitutions.length) parts.push(constitutions.join(', '))
  if (tag) parts.push(`tagged “${tag}”`)
  return parts.length ? `Clients: ${parts.join(' · ')}` : 'Every client on your books'
}
